import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { FadeIn, MaskedLine, MonoLabel } from "../components/motion";
import { api } from "../lib/api";

const inputCls =
    "w-full bg-transparent border border-line px-4 py-4 text-sm text-white placeholder:text-white/30 focus:border-[#FF5C00] focus:outline-none transition-colors duration-300 rounded-none";

const PROJECT_TYPES = ["Roading", "Civil Construction", "Earthworks", "Drainage", "Ground Stabilisation", "Infrastructure", "Other"];

const Contact = () => {
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState({
        name: "", company: "", email: "", phone: "",
        project_type: "", product_required: searchParams.get("product") || "", message: "",
    });
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const fileRef = useRef(null);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const data = new FormData();
            Object.entries(form).forEach(([k, v]) => data.append(k, v));
            if (file) data.append("file", file);
            await api.post("/contact", data, { headers: { "Content-Type": "multipart/form-data" } });
            setSuccess(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not send enquiry. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="border-b border-line px-4 md:px-8 pt-40 pb-14 md:pt-52 md:pb-20" data-testid="contact-hero">
                <FadeIn><MonoLabel>Contact / Enquiries</MonoLabel></FadeIn>
                <h1 className="mt-6 font-heading font-black uppercase tracking-tighter leading-[0.9] text-[11vw] md:text-[7vw]" data-testid="contact-headline">
                    <MaskedLine delay={0.15}>Let's find the</MaskedLine>
                    <MaskedLine delay={0.27}><span className="text-[#FF5C00]">right solution.</span></MaskedLine>
                </h1>
                <FadeIn delay={0.35} className="mt-8 max-w-xl">
                    <p className="text-white/70 leading-relaxed text-base md:text-lg">
                        Whether you know exactly what you need or need help choosing the right product, our team is here to help.
                    </p>
                </FadeIn>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-5 border-b border-line">
                <div className="lg:col-span-3 p-8 md:p-16 border-b lg:border-b-0 lg:border-r border-line">
                    {success ? (
                        <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center" data-testid="contact-success-message">
                            <CheckCircle2 className="w-12 h-12 text-[#FF5C00]" />
                            <h2 className="mt-6 font-heading font-extrabold uppercase tracking-tight text-3xl">Enquiry Sent</h2>
                            <p className="mt-4 text-white/70 max-w-sm leading-relaxed">
                                Thanks for your enquiry. Our team will get back to you shortly.
                            </p>
                            <button
                                data-testid="contact-send-another-button"
                                onClick={() => { setSuccess(false); setForm({ name: "", company: "", email: "", phone: "", project_type: "", product_required: "", message: "" }); setFile(null); }}
                                className="mt-10 px-10 h-12 border border-white/20 font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors duration-300"
                            >
                                Send Another Enquiry
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <input required data-testid="contact-name-input" placeholder="Name *" value={form.name} onChange={update("name")} className={inputCls} />
                                <input data-testid="contact-company-input" placeholder="Company" value={form.company} onChange={update("company")} className={inputCls} />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <input required type="email" data-testid="contact-email-input" placeholder="Email *" value={form.email} onChange={update("email")} className={inputCls} />
                                <input required data-testid="contact-phone-input" placeholder="Phone *" value={form.phone} onChange={update("phone")} className={inputCls} />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <select
                                    data-testid="contact-project-type-select"
                                    value={form.project_type}
                                    onChange={update("project_type")}
                                    className={`${inputCls} appearance-none bg-[#050505] ${form.project_type ? "text-white" : "text-white/30"}`}
                                >
                                    <option value="" disabled>Project Type</option>
                                    {PROJECT_TYPES.map((t) => <option key={t} value={t} className="text-white">{t}</option>)}
                                </select>
                                <input data-testid="contact-product-input" placeholder="Product / Service Required" value={form.product_required} onChange={update("product_required")} className={inputCls} />
                            </div>
                            <textarea required data-testid="contact-message-input" placeholder="Message *" rows={5} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} />

                            <div>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    data-testid="contact-file-input"
                                    className="hidden"
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                />
                                {file ? (
                                    <div className="flex items-center justify-between border border-line px-4 py-4" data-testid="contact-file-selected">
                                        <span className="font-mono text-xs text-white/70 truncate">{file.name}</span>
                                        <button type="button" data-testid="contact-file-remove" onClick={() => { setFile(null); fileRef.current.value = ""; }} className="text-white/40 hover:text-[#FF5C00] transition-colors" aria-label="Remove file">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        data-testid="contact-file-upload-button"
                                        onClick={() => fileRef.current?.click()}
                                        className="w-full border border-dashed border-white/20 px-4 py-8 flex flex-col items-center gap-3 text-white/40 hover:border-[#FF5C00] hover:text-white transition-colors duration-300"
                                    >
                                        <Upload className="w-5 h-5" />
                                        <span className="font-mono text-[10px] tracking-[0.25em] uppercase">Attach plans, drawings or specs (optional)</span>
                                    </button>
                                )}
                            </div>

                            <button
                                type="submit"
                                data-testid="contact-send-button"
                                disabled={submitting}
                                className="group w-full h-16 flex items-center justify-center gap-4 bg-[#FF5C00] text-white font-mono text-xs tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50"
                            >
                                {submitting ? "Sending…" : "Send Enquiry"}
                                {!submitting && <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />}
                            </button>
                        </form>
                    )}
                </div>

                <div className="lg:col-span-2 flex flex-col">
                    <div className="p-8 md:p-16 border-b border-line flex-1">
                        <MonoLabel>Direct Contact</MonoLabel>
                        <ul className="mt-8 space-y-6" data-testid="contact-details">
                            <li>
                                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">Sales Team</p>
                                <div className="mt-2 space-y-3">
                                    <p className="font-heading font-bold text-lg" data-testid="contact-sales-regan">
                                        Regan Cording <a href="tel:+64272068520" data-testid="contact-call-regan" className="font-mono text-sm text-[#FF5C00] font-normal ml-2 hover:underline underline-offset-4">027 206 8520</a>
                                    </p>
                                    <p className="font-heading font-bold text-lg" data-testid="contact-sales-hector">
                                        Hector Grant <a href="tel:+64273404464" data-testid="contact-call-hector" className="font-mono text-sm text-[#FF5C00] font-normal ml-2 hover:underline underline-offset-4">027 340 4464</a>
                                    </p>
                                </div>
                            </li>
                            <li>
                                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">Email</p>
                                <p className="mt-1 font-heading font-bold text-lg" data-testid="contact-email-display">
                                    <a href="mailto:sales@solidgroundsolutions.co.nz" data-testid="contact-email-link" className="hover:text-[#FF5C00] transition-colors duration-300">sales@solidgroundsolutions.co.nz</a>
                                </p>
                            </li>
                            <li>
                                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">Address</p>
                                <p className="mt-1 font-heading font-bold text-lg" data-testid="contact-address-display">684 Tremaine Ave, Palmerston North, NZ</p>
                            </li>
                            <li>
                                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">Business Hours</p>
                                <p className="mt-1 font-heading font-bold text-lg" data-testid="contact-hours-display">Mon–Fri 9:00am – 5:00pm</p>
                            </li>
                        </ul>
                    </div>
                    <div className="relative flex-1 min-h-[280px]" data-testid="contact-map">
                        <iframe
                            title="Solid Ground Solutions location map (placeholder)"
                            src="https://www.openstreetmap.org/export/embed.html?bbox=175.58%2C-40.39%2C175.65%2C-40.33&layer=mapnik&marker=-40.3564%2C175.6111"
                            className="absolute inset-0 w-full h-full grayscale invert-[0.9] contrast-[0.9]"
                            loading="lazy"
                        />
                        <span className="absolute bottom-3 left-3 font-mono text-[10px] tracking-[0.2em] uppercase bg-black text-white px-3 py-1">
                            Map — 684 Tremaine Ave, Palmerston North
                        </span>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
